//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { add, fromUnixTime, getUnixTime, isAfter, isBefore, isSameDay, max, sub } from 'date-fns';
import { useUrlState } from 'hooks/useUrlState';
import { useCallback, useEffect, useState } from 'react';
import { Range } from 'types';

const POLLING_INTERVAL = 30 * 1000; // 30sec

const determineValidTimerange = ({ now, initialRange, defaultRangeInHours }): Range => {
  let fromDate: Date;
  let toDate: Date;
  let isRelative = initialRange?.isRelative === true;

  // Handle only "from" present in initialRange
  if (initialRange?.from && !initialRange.to) {
    if (isAfter(now, initialRange.from)) {
      toDate = now;
      fromDate = max([sub(toDate, { hours: defaultRangeInHours }), initialRange.from]);
      if (isAfter(sub(toDate, { hours: defaultRangeInHours }), initialRange.from)) {
        isRelative = true;
      }
    } else {
      fromDate = new Date(initialRange.from);
      toDate = add(fromDate, { hours: defaultRangeInHours });
    }

    // Handle both "from" and "to" present in initialRange
  } else if (initialRange?.from && initialRange.to) {
    if (isAfter(now, initialRange.to) || isSameDay(now, initialRange.to)) {
      toDate = new Date(initialRange.to);
      fromDate = sub(toDate, { hours: defaultRangeInHours });
    } else if (isBefore(now, initialRange.from)) {
      fromDate = new Date(initialRange.from);
      toDate = add(fromDate, { hours: defaultRangeInHours });
    } else {
      toDate = now;
      fromDate = sub(toDate, { hours: defaultRangeInHours });
      isRelative = true;
    }

    // Ensure fromDate never before initialRange.from
    fromDate = max([fromDate, initialRange.from]);
  } else {
    // Handle no initialRange
    toDate = now;
    fromDate = sub(toDate, { hours: defaultRangeInHours });
    isRelative = true;
  }

  return { from: fromDate, to: toDate, isRelative };
};

export const useTimerange = (availableRange?: Range, defaultRangeInHours = 24 * 7) => {
  const [now, setNow] = useState(new Date());
  const { setUrlState, clearUrlState, getUrlState } = useUrlState();
  const initialRange = determineValidTimerange({ now, initialRange: availableRange, defaultRangeInHours });
  const [range, setRange] = useState<Range>(initialRange);

  const applyRange = useCallback(
    ({ from, to, isRelative }: Range) => {
      if (!from || !to) {
        console.error(`Invalid date range: from=${from}, to=${to}.`);
        resetRange();
        return;
      }

      setUrlState(
        {
          from: getUnixTime(from).toString(),
          to: getUnixTime(to).toString(),
          relative: isRelative || null,
        },
        true
      );
      setRange({ from, to, isRelative });
    },
    [setUrlState, setRange]
  );

  const resetRange = useCallback(() => {
    clearUrlState(['from', 'to', 'relative']);
    applyRange(initialRange);
  }, [clearUrlState, now, initialRange, applyRange]);

  const updateRelativeRange = useCallback(() => {
    const now = new Date();
    setNow(now);
    setRange((prevRange) => {
      if (!prevRange.to || !prevRange.from) {
        return prevRange;
      }
      const diffInSec = getUnixTime(prevRange.to) - getUnixTime(prevRange.from);
      const newFrom = new Date(now.getTime() - diffInSec * 1000);

      const updatedRange = {
        from: newFrom,
        to: now,
        isRelative: getUrlState().relative === 'true' ? true : false,
      };

      return updatedRange;
    });
  }, [applyRange, setNow]);

  useEffect(() => {
    const urlParams = getUrlState();
    const tsFrom = parseInt(urlParams.from);
    const tsTo = parseInt(urlParams.to);
    const isRelative = urlParams.relative === 'true';

    if (tsFrom && tsTo) {
      const from = fromUnixTime(tsFrom);
      const to = fromUnixTime(tsTo);
      applyRange({ from, to, isRelative });
    } else {
      applyRange(range);
    }
  }, [getUrlState, applyRange]);

  useEffect(() => {
    const isRelative = getUrlState().relative;
    if (!isRelative) {
      return;
    }

    updateRelativeRange();

    const intervalId = setInterval(updateRelativeRange, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [updateRelativeRange]);

  return {
    range,
    applyRange,
    resetRange,
    now,
    setNow,
  };
};
